import React, { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getApiUrl } from "../../../../config/api";
import { PublicPageShell, t } from "../shared/trendReportShared";

// Where the "Unsubscribe" link in the daily brief's footer lands. Public - the
// reader may have no account. It asks for the address the brief was sent to
// and checks it against this link before anything changes, so whoever holds a
// forwarded copy cannot opt the original recipient out by accident. The
// backend sends the opted-out address a notice with an undo link either way;
// see the unsubscribe routes in backend/domains/trend_reports/trend_reports_routes.py.
//
// step: "form" -> "confirm" -> "done", or "mismatch" / "invalid" / "error".

const INPUT =
  "w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent";
const PRIMARY =
  "w-full p-3 bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white font-bold rounded-md transition-colors duration-200";
const SECONDARY =
  "w-full p-3 text-gray-600 hover:bg-gray-100 font-medium rounded-md transition-colors duration-200";

const Title = ({ children }) => (
  <h1 className="text-xl font-bold text-gray-900 mb-3">{children}</h1>
);
const Body = ({ children }) => (
  <p className="text-sm text-gray-600 leading-relaxed mb-4">{children}</p>
);

export default function UnsubscribePage() {
  const { token } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const lang =
    new URLSearchParams(location.search).get("lang") === "zh" ? "zh" : "en";
  const setLang = (next) =>
    navigate({ search: `?lang=${next}` }, { replace: true });

  const [email, setEmail] = useState("");
  const [step, setStep] = useState("form");
  const [busy, setBusy] = useState(false);

  const post = async (action) => {
    const res = await fetch(
      `${getApiUrl()}/api/v2/trend-reports/unsubscribe/${encodeURIComponent(token)}/${action}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      },
    );
    return res.json().catch(() => ({}));
  };

  // Shared by both calls: a mismatch or a dead link can come back from either,
  // since /confirm re-checks the address itself.
  const settle = (data, onSuccess) => {
    if (data.success) onSuccess(data);
    else if (data.match === false) setStep("mismatch");
    else if (data.not_found) setStep("invalid");
    else setStep("error");
  };

  const onContinue = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      settle(await post("check"), (data) =>
        setStep(data.match ? "confirm" : "mismatch"),
      );
    } catch {
      setStep("error");
    } finally {
      setBusy(false);
    }
  };

  const onConfirm = async () => {
    setBusy(true);
    try {
      settle(await post("confirm"), () => setStep("done"));
    } catch {
      setStep("error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <PublicPageShell lang={lang} onLang={setLang}>
      {step === "form" && (
        <form onSubmit={onContinue} className="flex flex-col">
          <Title>{t("unsubTitle", lang)}</Title>
          <Body>{t("unsubIntro", lang)}</Body>
          <label className="text-gray-700 font-medium mb-2" htmlFor="unsub-email">
            {t("unsubEmailLabel", lang)}
          </label>
          <input
            id="unsub-email"
            type="email"
            required
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("unsubEmailPlaceholder", lang)}
            className={INPUT}
          />
          <button type="submit" disabled={busy} className={`${PRIMARY} mt-5`}>
            {busy ? t("unsubChecking", lang) : t("unsubContinue", lang)}
          </button>
        </form>
      )}

      {step === "confirm" && (
        <div className="flex flex-col">
          <Title>{t("unsubConfirmTitle", lang)}</Title>
          <p className="text-sm font-semibold text-gray-900 bg-slate-100 rounded-md px-3 py-2 mb-4 break-all">
            {email.trim()}
          </p>
          <Body>{t("unsubConfirmBody", lang)}</Body>
          <button type="button" disabled={busy} onClick={onConfirm} className={PRIMARY}>
            {busy ? t("unsubWorking", lang) : t("unsubConfirmButton", lang)}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => setStep("form")}
            className={`${SECONDARY} mt-2`}
          >
            {t("pubBack", lang)}
          </button>
        </div>
      )}

      {step === "mismatch" && (
        <div className="flex flex-col">
          <Title>{t("unsubMismatchTitle", lang)}</Title>
          <Body>{t("unsubMismatchBody", lang)}</Body>
          <Body>{t("unsubMismatchOwnCopy", lang)}</Body>
          <button type="button" onClick={() => setStep("form")} className={SECONDARY}>
            {t("unsubTryAgain", lang)}
          </button>
        </div>
      )}

      {step === "done" && (
        <div>
          <Title>{t("unsubDoneTitle", lang)}</Title>
          <Body>{t("unsubDoneBody", lang)}</Body>
        </div>
      )}

      {step === "invalid" && (
        <div>
          <Title>{t("pubInvalidTitle", lang)}</Title>
          <Body>{t("unsubInvalidBody", lang)}</Body>
        </div>
      )}

      {step === "error" && (
        <div className="flex flex-col">
          <Title>{t("pubErrorTitle", lang)}</Title>
          <Body>{t("pubErrorBody", lang)}</Body>
          <button type="button" onClick={() => setStep("form")} className={SECONDARY}>
            {t("pubBack", lang)}
          </button>
        </div>
      )}
    </PublicPageShell>
  );
}
